import React, { useCallback, useContext } from "react";

import { faCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCog, faKeyboard, faMinusCircle, faSlidersH } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { CalendarFilterContext, CalendarPageContext } from "~/calendar/CalendarPage";
import AppContext from "~/common/AppContext";
import { zindexRadixPopper } from "~/css/zindexes";
import useDebouncedValue from "~/lib/hooks/useDebouncedValue";
import useEffectAfterMount from "~/lib/hooks/useEffectAfterMount";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { capitalizeFirstLetter, useNewest } from "~/lib/utils";
import type { FiltersState, nonKeywords } from "~/settings/EventFilters";
import { Button, type CheckboxProps, Tooltip } from "~/ui";
import Menu from "~/ui/menu";

import styles from "./RightToolbar.module.css";

export const ViewOptionsMenuTrigger = ({ isOpen }: { isOpen: boolean }) => (
	<Button
		size="slim"
		variant="secondary"
		className={cx(styles.triggerButton, { [styles.active]: isOpen })}
		title="Calendar options"
	>
		<FontAwesomeIcon icon={faSlidersH} />
		&ensp;View
	</Button>
);

export default function ViewOptionsMenu() {
	const {
		eventFilterOptions,
		filters: _filters,
		updateFilters,
		showWeekends,
		toggleShowWeekends: _toggleShowWeekends,
		showTileIcons,
		toggleShowTileIcons: _toggleShowTileIcons,
	} = useContext(CalendarFilterContext);
	const { setShortcutsModalOpen } = useContext(CalendarPageContext);
	const openShortcutsSheet = React.useCallback(() => setShortcutsModalOpen(true), [setShortcutsModalOpen]);
	const [isOpen, _setOpen] = React.useState(false);
	const [uiFilters, setUiFilters] = React.useState<FiltersState>(_filters);
	const filters = useNewest(_filters, uiFilters);

	const setOpen = React.useCallback((open: boolean) => {
		_setOpen(open);
		dispatchAnalyticsTrack("View options", { feature: "Calendar", action: "set open", value: open });
	}, []);

	const onCheckboxChange = React.useCallback<(filterName: nonKeywords) => CheckboxProps["onCheckedChange"]>(
		filterName => checked => {
			setUiFilters({
				...filters,
				[filterName]: checked,
			});
			dispatchAnalyticsTrack("View options", {
				feature: "Calendar",
				action: "filter selected",
				key: filterName,
				value: checked,
			});
		},
		[filters],
	);

	const toggleShowWeekends = React.useCallback(() => {
		dispatchAnalyticsTrack("View options", {
			feature: "Calendar",
			action: "toggle show weekends",
			value: !showWeekends,
		});
		_toggleShowWeekends();
	}, [_toggleShowWeekends, showWeekends]);

	const toggleShowTileIcons = React.useCallback(() => {
		dispatchAnalyticsTrack("View options", {
			feature: "Calendar",
			action: "toggle show tile icons",
			value: !showTileIcons,
		});
		_toggleShowTileIcons();
	}, [_toggleShowTileIcons, showTileIcons]);

	const debouncedFilters = useDebouncedValue(uiFilters, 500);

	useEffectAfterMount(() => {
		updateFilters(debouncedFilters);
	}, [debouncedFilters, updateFilters]);

	const keywordTooltipContent = React.useMemo(() => {
		const keywordsForDisplay = filters["filterKeywords"] ? filters["filterKeywords"].join(", ") : "";

		const content =
			keywordsForDisplay.length === 0 ? (
				"No current keyword filters"
			) : (
				<>
					<b>Current keyword filters: </b>
					<p>{keywordsForDisplay}</p>
				</>
			);
		return <div className={styles.keywordTooltip}>{content}</div>;
	}, [filters]);

	const { openSettingsModal } = useContext(AppContext);
	const openPreferences = useCallback(() => {
		openSettingsModal("preferences");
	}, [openSettingsModal]);

	return (
		<Menu open={isOpen} onOpenChange={setOpen}>
			<Menu.Trigger>
				<span>
					<ViewOptionsMenuTrigger isOpen={isOpen} />
				</span>
			</Menu.Trigger>
			<Menu.Content>
				<Menu.Item onClick={toggleShowWeekends}>
					<div className={styles.weekMenuItem}>
						<div>Show weekends</div>
						{showWeekends && (
							<div className={styles.filterSelected}>
								<FontAwesomeIcon icon={faCheck} />
							</div>
						)}
					</div>
				</Menu.Item>
				<Menu.Item onClick={toggleShowTileIcons}>
					<div className={styles.weekMenuItem}>
						<div>Show event icons</div>
						{showTileIcons && (
							<div className={styles.filterSelected}>
								<FontAwesomeIcon icon={faCheck} />
							</div>
						)}
					</div>
				</Menu.Item>
				<Menu.Separator />
				<Menu.Sub>
					<Menu.SubTrigger>View settings</Menu.SubTrigger>
					<Menu.Portal>
						<Menu.SubContent>
							<Menu.Label>Show…</Menu.Label>

							{eventFilterOptions.map(option => (
								<Menu.CheckboxItem
									key={option.name}
									checked={Boolean(filters[option.name])}
									onCheckedChange={onCheckboxChange(option.name)}
								>
									{capitalizeFirstLetter(option.displayText)}
								</Menu.CheckboxItem>
							))}
						</Menu.SubContent>
					</Menu.Portal>
				</Menu.Sub>
				<Menu.Separator />
				<Menu.Item icon={faMinusCircle} onClick={openPreferences}>
					<Tooltip content={keywordTooltipContent} zIndex={zindexRadixPopper + 1} maxWidth={220}>
						<span>Hide by keyword…</span>
					</Tooltip>
				</Menu.Item>
				<Menu.Item icon={faCog} onClick={openPreferences}>
					Open calendar settings
				</Menu.Item>
				<Menu.Separator />
				<Menu.Item icon={faKeyboard} onSelect={openShortcutsSheet}>
					Keyboard shortcuts
				</Menu.Item>
			</Menu.Content>
		</Menu>
	);
}
